import { usePersonReadDataContext } from '@aibees/elephant/context';
import {
  PeopleTableInfo,
  Person,
  PersonPostionGroup,
} from '@aibees/elephant/feature/search-table-people/types';
import { groupBy, sortBy } from 'lodash-es';
import { useMemo } from 'react';
import { getJobDuration } from '../utils';

function useWorkHistoryData(personData?: Person | PeopleTableInfo | null) {
  const personContext = usePersonReadDataContext();
  return personData ?? personContext?.api?.data ?? null;
}

/** Returns only full work history array. Use when component only needs fullWorkHistory. */
export function useWorkHistoryFullHistory(
  personData?: Person | PeopleTableInfo | null
) {
  const data = useWorkHistoryData(personData);
  return useMemo(() => data?.positionGroups ?? [], [data?.positionGroups]);
}

/** Returns only all present roles. Use when component only needs allPresentRoles. */
export function useWorkHistoryPresentRoles(
  personData?: Person | PeopleTableInfo | null
): PersonPostionGroup[] | undefined {
  const data = useWorkHistoryData(personData);
  return useMemo(() => {
    const hasNoDates = data?.positionGroups?.every((w) => !w.date?.start);
    if (hasNoDates && data?.positionGroups?.length) {
      return [data.positionGroups[0]];
    }
    return (
      data?.positionGroups
        ?.filter((w) => !w.date?.end)
        .map((w) => ({
          ...w,
          profile_positions:
            w.profile_positions?.filter((p) => !p.date?.end) || [],
        }))
        .filter((w) => w.profile_positions.length > 0) || []
    );
  }, [data?.positionGroups]);
}

/** Returns only current roles in the current company. Use when component only needs currentRolesInCompany. */
export function useWorkHistoryCurrentRolesInCompany(
  personData?: Person | PeopleTableInfo | null
) {
  const data = useWorkHistoryData(personData);
  return useMemo(() => {
    if (!data?.company?.id) {
      return data?.positionGroups?.slice(0, 1);
    }
    return data?.positionGroups?.filter(
      (w) => !w.date?.end && w.company?.id === data?.company?.id
    );
  }, [data?.positionGroups, data?.company]);
}

/** Returns only all current roles (sorted). Use when component only needs allCurrentRoles. */
export function useWorkHistoryCurrentRoles(
  personData?: Person | PeopleTableInfo | null
) {
  const data = useWorkHistoryData(personData);
  const allPresentRoles = useWorkHistoryPresentRoles(personData);
  const allCurrentRolesGroupedByCompany = useMemo(
    () => (allPresentRoles ? groupBy(allPresentRoles, 'company.name') : {}),
    [allPresentRoles]
  );
  return useMemo(
    () =>
      sortBy(allCurrentRolesGroupedByCompany, (w) => {
        return data?.positionGroups?.findIndex(
          (h) => h.company?.name === w[0].company?.name
        );
      }),
    [data?.positionGroups, allCurrentRolesGroupedByCompany]
  );
}

/** Returns only full work exp duration. Use when component only needs fullWorkExpDuration. */
export function useWorkHistoryDuration(
  personData?: Person | PeopleTableInfo | null
) {
  const data = useWorkHistoryData(personData);
  return useMemo(() => {
    const fullWorkHistory = data?.positionGroups ?? [];
    if (!fullWorkHistory || fullWorkHistory.length === 0) return undefined;
    const earliestStartDate = fullWorkHistory
      .map((role) => role.date?.start)
      .filter(Boolean)
      .sort()[0];
    const latestEndDate = fullWorkHistory
      .map((role) => role.date?.end)
      .filter(Boolean)
      .sort()
      .pop();
    const hasCurrentRoles = fullWorkHistory.some((role) => !role.date?.end);
    const endDate = hasCurrentRoles ? null : latestEndDate;
    return getJobDuration(earliestStartDate, endDate);
  }, [data?.positionGroups]);
}

/** Returns only total present roles count. Use when component only needs totalPresentRolesCount. */
export function useWorkHistoryPresentCount(
  personData?: Person | PeopleTableInfo | null
) {
  const allPresentRoles = useWorkHistoryPresentRoles(personData);
  return useMemo(() => {
    if (!allPresentRoles) return 0;
    return allPresentRoles.reduce(
      (total, role) => total + (role.profile_positions?.length || 0),
      0
    );
  }, [allPresentRoles]);
}

export function useGetWorkHistory(
  personData?: Person | PeopleTableInfo | null
) {
  const fullWorkHistory = useWorkHistoryFullHistory(personData);
  const currentRolesInCompany = useWorkHistoryCurrentRolesInCompany(personData);
  const allCurrentRoles = useWorkHistoryCurrentRoles(personData);
  const allPresentRoles = useWorkHistoryPresentRoles(personData);
  const fullWorkExpDuration = useWorkHistoryDuration(personData);
  const totalPresentRolesCount = useWorkHistoryPresentCount(personData);

  return useMemo(
    () => ({
      fullWorkHistory,
      currentRolesInCompany,
      allCurrentRoles,
      allPresentRoles,
      fullWorkExpDuration,
      totalPresentRolesCount,
    }),
    [
      fullWorkHistory,
      currentRolesInCompany,
      allCurrentRoles,
      allPresentRoles,
      fullWorkExpDuration,
      totalPresentRolesCount,
    ]
  );
}
